/**
 * Concrete `AssetLibraryAdapter`s — one per owner kind. Each is memoized on
 * its owner id so the shared components see a stable object.
 */

import { useMemo } from 'react';
import { api } from '@/lib/api';
import {
  useArcheAssetsList,
  useUploadArcheAsset,
  usePatchArcheAsset,
  useSoftDeleteArcheAsset,
} from '../../hooks/useArcheAssetLibrary';
import {
  useWorkspaceAssetsList,
  useUploadWorkspaceAsset,
  usePatchWorkspaceAsset,
  useSoftDeleteWorkspaceAsset,
} from '../../hooks/useWorkspaceAssetLibrary';
import type { AssetLibraryAdapter } from './adapter';

/** The per-arche library (`/contributor/arches/:archeId/...`). */
export function useArcheAssetLibraryAdapter(archeId: string): AssetLibraryAdapter {
  return useMemo<AssetLibraryAdapter>(
    () => ({
      ownerKey: archeId,
      buildPreviewUrl: (assetId) => api.buildArcheAssetPreviewUrl(archeId, assetId),
      useAssetsList: (opts) => useArcheAssetsList(archeId, opts),
      useUpload: () => useUploadArcheAsset(archeId),
      usePatch: () => usePatchArcheAsset(archeId),
      useSoftDelete: () => useSoftDeleteArcheAsset(archeId),
    }),
    [archeId],
  );
}

/** A Workspace (org Holder) library (`/contributor/workspaces/:holderId/...`). */
export function useWorkspaceAssetLibraryAdapter(holderId: string): AssetLibraryAdapter {
  return useMemo<AssetLibraryAdapter>(
    () => ({
      ownerKey: holderId,
      buildPreviewUrl: (assetId) => api.buildWorkspaceAssetPreviewUrl(holderId, assetId),
      useAssetsList: (opts) => useWorkspaceAssetsList(holderId, opts),
      useUpload: () => useUploadWorkspaceAsset(holderId),
      usePatch: () => usePatchWorkspaceAsset(holderId),
      useSoftDelete: () => useSoftDeleteWorkspaceAsset(holderId),
    }),
    [holderId],
  );
}
